import { useState, useEffect } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";

const field = "w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-600";
const VTYPES = ["van", "sedan", "suv", "minivan", "wheelchair_van"];
const EMPTY = { fleet_number: "", vin: "", year: "", make: "", model: "", plate: "", mileage: "", vehicle_type: "van", wheelchair_accessible: false, capacity: "", status: "active" };

export default function VehicleFormDialog({ vehicle, open, onOpenChange, onSaved }) {
  const editing = !!vehicle?.id;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(vehicle?.id ? {
      fleet_number: vehicle.fleet_number || "", vin: vehicle.vin || "", year: vehicle.year ?? "",
      make: vehicle.make || "", model: vehicle.model || "", plate: vehicle.plate || "",
      mileage: vehicle.mileage ?? "", vehicle_type: vehicle.vehicle_type || "van",
      wheelchair_accessible: !!vehicle.wheelchair_accessible, capacity: vehicle.capacity ?? "",
      status: vehicle.status || "active",
    } : EMPTY);
  }, [open, vehicle]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.fleet_number.trim()) { toast.error("Fleet number is required."); return; }
    for (const [k, label] of [["year", "Year"], ["mileage", "Mileage"], ["capacity", "Capacity"]]) {
      if (form[k] !== "" && isNaN(Number(form[k]))) { toast.error(`${label} must be a number.`); return; }
    }
    setSaving(true);
    const payload = {
      ...form,
      year: form.year === "" ? null : Number(form.year),
      mileage: form.mileage === "" ? 0 : Number(form.mileage),
      capacity: form.capacity === "" ? 0 : Number(form.capacity),
    };
    try {
      if (editing) await fleetApi.put(`/vehicles/${vehicle.id}`, payload);
      else await fleetApi.post("/vehicles", payload);
      toast.success(editing ? "Vehicle updated." : "Vehicle added.");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const code = e.response?.status;
      toast.error(code === 403 ? "You don't have permission to manage vehicles."
        : fleetError(e, "Save failed."));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-800 bg-[#0f172a] text-white" data-testid="vehicle-form-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit vehicle" : "Add vehicle"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Fleet # *</label>
            <Input value={form.fleet_number} onChange={set("fleet_number")} className={field} data-testid="vehicle-fleet-number" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Plate</label>
            <Input value={form.plate} onChange={set("plate")} className={field} data-testid="vehicle-plate" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Year</label>
            <Input type="number" value={form.year} onChange={set("year")} className={field} data-testid="vehicle-year" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">VIN</label>
            <Input value={form.vin} onChange={set("vin")} className={field} data-testid="vehicle-vin" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Make</label>
            <Input value={form.make} onChange={set("make")} className={field} data-testid="vehicle-make" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Model</label>
            <Input value={form.model} onChange={set("model")} className={field} data-testid="vehicle-model" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Type</label>
            <select value={form.vehicle_type} onChange={set("vehicle_type")} className={field} data-testid="vehicle-type">
              {VTYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Capacity</label>
            <Input type="number" value={form.capacity} onChange={set("capacity")} className={field} data-testid="vehicle-capacity" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Mileage</label>
            <Input type="number" value={form.mileage} onChange={set("mileage")} className={field} data-testid="vehicle-mileage" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Status</label>
            <select value={form.status} onChange={set("status")} className={field} data-testid="vehicle-status">
              <option value="active">active</option><option value="maintenance">maintenance</option><option value="out_of_service">out of service</option>
            </select>
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <Checkbox id="veh-wc" checked={form.wheelchair_accessible} onCheckedChange={(v) => setForm({ ...form, wheelchair_accessible: v === true })} data-testid="vehicle-wheelchair" />
            <label htmlFor="veh-wc" className="text-sm text-slate-300">Wheelchair accessible</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="vehicle-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="gap-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="vehicle-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing ? "Save" : "Add vehicle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
